import React, { useState, useEffect, Suspense } from 'react';
import './App.scss';
import { Header } from './../Core/Header';
import AppContext from './AppContext';
import { AppStorage } from '../Core/Service/storage-service';
import { Toast } from './../Core/Toast';
import { get, httpVerbEmitter } from '../Core/Service/http-calls';
import { ProfileModal } from '../Core/Modal';
import { getAllManageAccountMenus, getAllowedWorkflow, getGridFilterList } from '../Core/Service/workflow-service';
import { Customi18 } from '../Core/Controls/i18react/customei18';
import { getMenuItems } from '../Core/Service/menuService';
import DialogComp from '../Core/Modal/dialogModal';
import DisplayMessage from '../Core/Modal/DisplayMessage';

const Loader = () => (
    <div className="App">
        <div>loading...</div>
    </div>
);

function App() {
    const urlParams = new URLSearchParams(window.location.search.toLowerCase());
    const fromStep = urlParams.get('fromstep') === 'true';

    const portalSettings = AppStorage.getPortalSetting() || { expired: true };
    const isWIPAllowed = AppStorage.getWIPAllowed() && portalSettings.portalSettingeWIPEnabled;
    const isReportingAllowed = AppStorage.getReportingAllowed() && portalSettings.portalSettingReportingEnabled;
    const isAppraisalAllowed = AppStorage.getAppraisalPortalAllowed() && portalSettings.portalSettingsAppraisalEnabled;

    const [contextState, setContextState] = useState({
        loggedIn: AppStorage.isLoggedIn(),
        userRoles: AppStorage.getUserRoles(),
        displayName: AppStorage.getName(),
        lastLogin: AppStorage.getLastLogin(),
        canOverrideMandatorySteps: AppStorage.getCanOverrideMandatorySteps(),
        canAssignJobsToAuditor: AppStorage.getCanAssignJobsToAuditor(),
        canResetPassword: AppStorage.getCanResetPassword(),
        portalSettings: portalSettings,
        snackbar_open: false,
        snackbar_message: '',
        visibleModalId: '',
        showTopNavAndMenu: fromStep ? false : true,
        isTopNav: true,
        showWelcomeDialog: false
    });
    const { i18n } = Customi18();
    const [tempMenus, setTempMenus] = useState({ menus: [], role: '', menuName: '' });
    const [reportingMenus, setReportingMenus] = useState({ menus: [], role: '', menuName: '' });
    const [appraisalMenu, setAppraisalMenu] = useState({ menus: [], role: '', menuName: '' });
    const [mAccountMenus, setMAccountMenus] = useState({ menus: [], role: '', menuName: '' });
    const [isAppReload, setIsAppReload] = useState(null);

    useEffect(() => {
        async function getPortalSetting() {
            if (portalSettings.expired) {
                const res = await get(`portalSetting`);
                if (res.data.details[0]) {
                    AppStorage.setPortalSetting(res.data.details[0]);
                    setContextState((st) => ({
                        ...st,
                        portalSettings: AppStorage.getPortalSetting()
                    }));
                }
            } else {
                setContextState((st) => ({
                    ...st,
                    portalSettings: AppStorage.getPortalSetting()
                }));
            }
        }
        getPortalSetting();

        const listenMsg = ({ data }) => {
            if (data.type === 'trigger-logout') {
                sessionStorage.removeItem('welcomeShown');
                AppStorage.performLogout();
                window.location = '/';
                window.location.reload(true);
            }
        };
        window.addEventListener('message', listenMsg);
        return () => window.removeEventListener('message', listenMsg);
    }, []);

    useEffect(() => {
        window.document.title = portalSettings.portalSettingTitle || 'eWIP';
    }, [contextState.portalSettings]);

    useEffect(() => {
        if (contextState.loggedIn && !sessionStorage.getItem('welcomeShown') && portalSettings.portalSettingDisplayMessage) {
            setContextState((st) => ({ ...st, showWelcomeDialog: true }));
        }
    }, [contextState.loggedIn]);

    useEffect(() => {
        const fetchData = async () => {
            if (!fromStep) {
                if (contextState.loggedIn && isWIPAllowed) {
                    await setUserPreference();
                }
                if (
                    contextState.loggedIn &&
                    (contextState.userRoles == 'internal' || contextState.userRoles == 'technician' || contextState.userRoles == 'admin')
                ) {
                    let maRes = await getAllManageAccountMenus();
                    setMAccountMenus({ menus: maRes.data?.list || [], menuName: 'maDashboard' });

                    let storedID = localStorage.getItem('_managementAccountDefaultDashboardID_');

                    // Check if storedID is not null/undefined/empty and is a valid number
                    if (maRes.data?.list.length > 0 && (storedID === null || storedID === undefined || storedID.trim() === '' || isNaN(Number(storedID)))) {
                        localStorage.setItem('_managementAccountDefaultDashboardID_', String(maRes.data?.list[0].id ?? ''));
                    }
                    if (isWIPAllowed) {
                        let res = await getAllowedWorkflow();
                        setTempMenus({ menus: res.data.list.table || [], menuName: 'Dashboard' });
                    }
                    let ReportsMenus = await getMenuItems();
                    if (isReportingAllowed) {
                        setReportingMenus({ menus: ReportsMenus.data?.list || [], menuName: 'Reporting' });
                    }
                    setAppraisalMenu({ menus: ReportsMenus?.data?.list?.filter((k) => k.parentMenu === 'Appraisal'), menuName: 'Appraisal' });
                }
            }
        };

        fetchData();
    }, []);

    const setUserPreference = async () => {
        const res = await getGridFilterList();
        if (res.success) {
            i18n.changeLanguage(res.data?.details[0]?.languageCode || 'en');
            localStorage.setItem('_userPreference_', JSON.stringify(res.data));
            setIsAppReload(new Date());
        }
    };

    const getUserPreference = () => JSON.parse(localStorage.getItem('_userPreference_'));

    return (
        <Suspense fallback={<Loader />}>
            <AppContext.Provider
                value={{
                    ...contextState,
                    showToast: (msg) => setContextState((st) => ({ ...st, snackbar_open: true, snackbar_message: msg })),
                    hideToast: () => setContextState((st) => ({ ...st, snackbar_open: false, snackbar_message: '' })),
                    showModal: (id) => setContextState((st) => ({ ...st, visibleModalId: id })),
                    hideModal: () => setContextState((st) => ({ ...st, visibleModalId: '' })),
                    hideTopNavAndMenu: () => setContextState((st) => ({ ...st, showTopNavAndMenu: false })),
                    hideShowTopNav: (val) => setContextState((st) => ({ ...st, isTopNav: val })),
                    tempMenus,
                    reportingMenus,
                    appraisalMenu,
                    setUserPreference,
                    getUserPreference,
                    isAppReload,
                    mAccountMenus
                }}
            >
                <div className="App">
                    <Header />
                    {contextState.showWelcomeDialog && (
                        <DialogComp
                            title="Important Message"
                            maxWidth="md"
                            fullWidth
                            onClose={() => {
                                sessionStorage.setItem('welcomeShown', 'true');
                                setContextState((st) => ({ ...st, showWelcomeDialog: false }));
                            }}
                        >
                            <DisplayMessage html={contextState.portalSettings.portalSettingDisplayMessage} />
                        </DialogComp>
                    )}
                </div>
                <Toast />
                <ProfileModal />
            </AppContext.Provider>
        </Suspense>
    );
}

export default App;
